const epInquiryLinkSelector = '[data-ep-inquiry-link]';
const epInquiryFormSelector = '[data-ep-inquiry-form]';
const epInitializedInquiryForms = new WeakSet();

function epVariantId(value) {
  const text = String(value ?? '').trim();
  return /^[1-9]\d{0,19}$/.test(text) ? text : '';
}

function epQuantity(value) {
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? String(number) : '';
}

function epProductHandle(value) {
  const text = String(value ?? '').trim();
  return /^[a-z0-9][a-z0-9-]{0,254}$/i.test(text) ? text : '';
}

function epLocaleRoot(config = {}) {
  const value = window.Shopify?.routes?.root || config.routesRoot || '/';
  const url = new URL(value, window.location.origin);
  if (url.origin !== window.location.origin) return '/';
  return url.pathname.replace(/\/?$/, '/');
}

function epCanonicalProductUrl(handle, variantId, root) {
  const url = new URL(root + 'products/' + encodeURIComponent(handle), window.location.origin);
  if (variantId) url.searchParams.set('variant', variantId);
  return url.href;
}

function epProductFormForLink(link) {
  if (link.hasAttribute('data-ep-inquiry-fixed-context')) return null;
  const handle = epProductHandle(link.dataset.epInquiryProduct);
  if (!handle) return null;
  const suffix = '/products/' + handle + '.js';
  const scopes = new Set([
    link.closest('[data-product-wrapper]'),
    link.closest('.shopify-section'),
    document
  ]);
  for (const scope of scopes) {
    if (!scope) continue;
    const matches = Array.from(scope.querySelectorAll('form[data-product-form]')).filter(form => {
      const productUrl = form.querySelector('variant-selection[product-url]')?.getAttribute('product-url');
      if (!productUrl) return false;
      try {
        const url = new URL(productUrl, window.location.origin);
        return url.origin === window.location.origin && url.pathname.endsWith(suffix);
      } catch {
        return false;
      }
    });
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) return null;
  }
  return null;
}

function epRefreshInquiryLink(link) {
  const form = epProductFormForLink(link);
  if (!form) return;
  const url = new URL(link.href, window.location.origin);
  if (url.origin !== window.location.origin) return;
  const variantField = form.querySelector('[name="id"]');
  const quantityField = form.querySelector('[name="quantity"]');
  if (variantField) {
    const id = epVariantId(variantField.value);
    if (id) url.searchParams.set('ep_variant', id);
    else url.searchParams.delete('ep_variant');
  }
  if (quantityField) {
    const quantity = epQuantity(quantityField.value);
    if (quantity) url.searchParams.set('ep_quantity', quantity);
    else url.searchParams.delete('ep_quantity');
  }
  link.href = url.pathname + url.search + url.hash;
}

async function epReadInquiryJson(path) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 8000);
  try {
    const response = await fetch(path, {
      method: 'GET',
      credentials: 'same-origin',
      signal: controller.signal,
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Inquiry context unavailable');
    return await response.json();
  } finally {
    window.clearTimeout(timeout);
  }
}

function epContextLines(config, title, options, quantity, url) {
  const lines = [];
  if (title) lines.push(config.productLabel + ': ' + title);
  if (options && options !== 'Default Title') lines.push(config.optionsLabel + ': ' + options);
  if (quantity) lines.push(config.quantityLabel + ': ' + quantity);
  if (url) lines.push(config.linkLabel + ': ' + url);
  return lines.join('\n');
}

async function epProductInquiryContext(params, config, root) {
  const handle = epProductHandle(params.get('ep_product'));
  if (!handle) return null;
  const variantId = epVariantId(params.get('ep_variant'));
  const quantity = epQuantity(params.get('ep_quantity'));
  const productUrl = epCanonicalProductUrl(handle, variantId, root);
  const fallback = {
    title: config.productTitle,
    message: epContextLines(config, '', '', quantity, productUrl),
    missingOptions: true
  };
  try {
    const product = await epReadInquiryJson(root + 'products/' + encodeURIComponent(handle) + '.js');
    if (typeof product.title !== 'string' || product.handle !== handle || !Array.isArray(product.variants)) {
      return fallback;
    }
    const variant = variantId
      ? product.variants.find(item => String(item.id) === variantId)
      : (product.variants.length === 1 ? product.variants[0] : null);
    const options = typeof variant?.title === 'string' ? variant.title : '';
    return {
      title: product.title,
      message: epContextLines(config, product.title, options, quantity, productUrl),
      missingOptions: !variant && (Boolean(variantId) || product.variants.length > 1)
    };
  } catch {
    return fallback;
  }
}

function epCartProductUrl(item) {
  if (typeof item.url !== 'string') return '';
  const url = new URL(item.url, window.location.origin);
  if (url.origin !== window.location.origin || !/\/products\/[^/]+$/.test(url.pathname)) return '';
  url.search = '';
  url.hash = '';
  const variantId = epVariantId(item.variant_id || item.id);
  if (variantId) url.searchParams.set('variant', variantId);
  return url.href;
}

async function epCartInquiryContext(config, root) {
  const cart = await epReadInquiryJson(root + 'cart.js');
  if (!Array.isArray(cart.items)) return null;
  if (!cart.items.length) return { empty: true };
  const messages = cart.items.map((item, index) => {
    const title = typeof item.product_title === 'string' ? item.product_title : String(item.title || '');
    const options = typeof item.variant_title === 'string' ? item.variant_title : '';
    return String(index + 1) + '. ' + epContextLines(
      config,
      title,
      options,
      epQuantity(item.quantity),
      epCartProductUrl(item)
    );
  });
  return { title: config.cartTitle, message: messages.join('\n\n') };
}

function epApplyInquiryTopic(section, params) {
  const topics = {
    fitment: 'Fitment help',
    shipping: 'Shipping or freight',
    order: 'Order support',
    build: 'Sandcar build',
    fabrication: 'Custom fabrication',
    other: 'Other'
  };
  const topic = section.querySelector('#ep_contact_topic');
  const value = topics[params.get('ep_topic')];
  if (topic && value) topic.value = value;
  const platform = section.querySelector('#ep_build_platform');
  const selectedPlatform = params.get('ep_platform');
  if (platform && ['XR2', 'DR1'].includes(selectedPlatform)) platform.value = selectedPlatform;
}

async function epInitializeInquiryForm(section) {
  if (epInitializedInquiryForms.has(section)) return;
  epInitializedInquiryForms.add(section);
  const feedback = section.querySelector('[data-ep-inquiry-feedback]');
  if (feedback) window.requestAnimationFrame(() => feedback.focus());
  if (section.querySelector('[data-ep-inquiry-success]')) return;

  const body = section.querySelector('[name="contact[body]"]');
  const panel = section.querySelector('[data-ep-inquiry-context]');
  const title = section.querySelector('[data-ep-inquiry-context-title]');
  const status = section.querySelector('[data-ep-inquiry-context-status]');
  const addButton = section.querySelector('[data-ep-inquiry-context-add]');
  const configElement = section.querySelector('[data-ep-inquiry-config]');
  if (!body || !panel || !title || !status || !addButton || !configElement) return;
  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  const hasErrors = Boolean(section.querySelector('[data-ep-inquiry-errors]'));
  if (!hasErrors) epApplyInquiryTopic(section, params);
  const fromCart = ['freight', 'shipping'].includes(params.get('ep_cart'));
  const fromProduct = epProductHandle(params.get('ep_product'));
  if (!fromCart && !fromProduct) return;

  let edited = false;
  body.addEventListener('input', () => { edited = true; });
  panel.hidden = false;
  title.textContent = fromCart ? config.cartTitle : config.productTitle;
  status.textContent = config.loading;
  const root = epLocaleRoot(config);
  let context;
  try {
    context = fromCart
      ? await epCartInquiryContext(config, root)
      : await epProductInquiryContext(params, config, root);
  } catch {
    status.textContent = config.manual;
    return;
  }
  if (!context || context.empty || !context.message) {
    status.textContent = context?.empty ? config.emptyCart : config.manual;
    return;
  }

  title.textContent = context.title;
  const addedStatus = config.added + (context.missingOptions ? ' ' + config.missingOptions : '');
  const markFilled = () => {
    body.classList.add('form-field-filled');
    body.dispatchEvent(new Event('input', { bubbles: true }));
  };
  if (!hasErrors && !edited && !body.value.trim()) {
    body.value = context.message + '\n\n';
    markFilled();
    status.textContent = addedStatus;
    return;
  }
  if (body.value.includes(context.message)) {
    status.textContent = addedStatus;
    return;
  }

  status.textContent = config.available;
  addButton.hidden = false;
  addButton.addEventListener('click', () => {
    if (!body.value.includes(context.message)) {
      body.value = body.value.trimEnd() + (body.value.trim() ? '\n\n' : '') + context.message + '\n\n';
      markFilled();
    }
    addButton.hidden = true;
    status.textContent = addedStatus;
    body.focus();
  });
}

function epInitializeInquiries(container = document) {
  container.querySelectorAll(epInquiryLinkSelector).forEach(epRefreshInquiryLink);
  container.querySelectorAll(epInquiryFormSelector).forEach(epInitializeInquiryForm);
}

function epRefreshInquiryAtEvent(event) {
  const link = event.target instanceof Element ? event.target.closest(epInquiryLinkSelector) : null;
  if (link) epRefreshInquiryLink(link);
}

document.addEventListener('click', epRefreshInquiryAtEvent, true);
document.addEventListener('pointerdown', epRefreshInquiryAtEvent, { capture: true, passive: true });
document.addEventListener('focusin', epRefreshInquiryAtEvent);
document.addEventListener('change', event => {
  const wrapper = event.target instanceof Element ? event.target.closest('[data-product-wrapper]') : null;
  wrapper?.querySelectorAll(epInquiryLinkSelector).forEach(epRefreshInquiryLink);
});
document.addEventListener('shopify:section:load', event => epInitializeInquiries(event.target));

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => epInitializeInquiries(), { once: true });
} else {
  epInitializeInquiries();
}
